<template>
  <div class="border border-border-blue rounded-md flex flex-col mb-4">
    <div class="flex items-center mb-5 px-5 pt-5">
      <InlineSvg :src="IconDoneStep" />
      <div class="text-main text-xl font-semibold ml-2">Dictation</div>
    </div>
    <div class="flex-1 overflow-auto px-5 pb-3">
      <el-skeleton v-if="isLoading || isRefetching" :rows="rows" animated class="p-5" />
      <div v-else>
        <el-table
          :data="data"
          class="advisor-table"
          row-class-name="row-class"
          header-cell-class-name="custom-header-table"
          style="width: 100%"
          empty-text="No Records Found."
          row-key="id"
          border
          @sort-change="change"
        >
          <el-table-column prop="name" label="Dictation Name" min-width="250">
            <template #default="scope">
              <div class="text-sm text-gray-700 break-words" :class="{ 'cursor-pointer': isCursor }">
                {{ scope.row.name || '-' }}
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="date" label="Meeting Date" min-width="160" sortable>
            <template #default="scope">
              <span class="text-sm text-gray-700 break-words">{{ scope.row.date || '-' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="advisor" label="Advisor" min-width="180">
            <template #default="scope">
              <span class="text-sm text-gray-700 break-words">
                {{ scope.row.advisor ? scope.row.advisor.first_name + ' ' + scope.row.advisor.last_name : '-' }}
              </span>
            </template>
          </el-table-column>

          <el-table-column prop="created_at" label="Created Date" min-width="150" sortable>
            <template #default="scope">
              <span class="text-sm text-gray-700 break-words">{{ formatDate(scope.row.created_at) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useFetchMeetingNotesList } from '@/api/use-fetch-meeting-notes-list'
import IconDoneStep from '@/assets/svg/icon-done-step.svg'
import { usePaginationData } from '@/utils/use-pagination-data.js'

export default defineComponent({
  name: 'NotesInfo',

  props: {
    memberData: {
      type: Object,
      required: true,
    },
  },
  emits: ['edit-event', 'delete-event', 'open-new-event'],

  setup(props, { emit }) {
    const search = ref('')
    const route = useRoute()
    const router = useRouter()
    const rows = 8
    const currentPage = encodeURI(route.params.p || 1)
    const { paginationData, handlePaginationChange } = usePaginationData()

    paginationData.page = currentPage

    const {
      isLoading,
      isError,
      data,
      pagination,
      refetch: refetchTasks,
      isRefetching,
    } = useFetchMeetingNotesList({
      page: { page: paginationData.page },
      search: search,
      advisor_id: props.memberData.id,
    })

    const isCursor = computed(() => true)

    const formatDate = (dateStr) => {
      if (!dateStr) {
        return '-'
      }
      const date = new Date(dateStr)
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const handleEditEvent = (eventId) => {
      emit('edit-event', eventId)
    }

    const deleteEventById = (eventId) => {
      emit('delete-event', eventId)
    }

    const openNewMeetingNotes = () => {
      emit('open-new-event')
    }

    const handleActionSelect = (id, action) => {
      switch (action) {
        case 'edit':
          handleEditEvent(id)
          break
        case 'delete':
          deleteEventById(id)
          break
        default:
          console.log('Action not defined')
      }
    }

    const viewAll = () => {
      router.push({
        name: `${route.meta.type}/meeting-notes`,
        params: { id: route.params.id },
      })
    }

    return {
      isLoading,
      isError,
      data,
      pagination,
      rows,
      refetchTasks,
      isRefetching,
      formatDate,
      IconDoneStep,
      handleEditEvent,
      deleteEventById,
      openNewMeetingNotes,
      handleActionSelect,
      viewAll,
      isCursor,
      handlePaginationChange,
      showViewAll: computed(() => true),
    }
  },
})
</script>

<style scoped>
.row-class {
  cursor: pointer;
}
</style>
